import React, { useState, useEffect, useCallback } from "react";
import Marquee from "react-fast-marquee";
import AppTooltip from "../ui/AppTooltip";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { MarketStatus } from "./MarketStatus";
import { StatusMessage } from "./StatusMessage";
import { MarqueeItem } from "./MarqueeItem";
import { UseResponsiveStatus } from "../../hooks/UseResponsiveStatus";
import { MOBILE_BREAKPOINT } from "../../constants";
import { stockActions } from "../../redux/features/stock/stockSlice";
import "./Marquee.css";
import stockDetailsData from "../../data/bse_stocks_website_urls.json";
import { fetchGrowwQuotesBatch } from "../../api/groww_proxy";
import { fetchMarketHolidayStatus } from "../../api/marketHolidayStatus";

const MARQUEE_SECURITIES = ["RELIANCE", "TCS", "HDFCBANK", "INFY"].map(
  (symbol) => ({
    exchange: "NSE",
    segment: "CASH",
    trading_symbol: symbol,
  })
);
const REFRESH_INTERVAL_MS = 10000;

const mapGrowwQuotesToMarqueeItems = (quotes = []) => {
  const securityOrder = new Map(
    MARQUEE_SECURITIES.map((security, index) => [security.trading_symbol, index])
  );

  return quotes
    .filter((quote) => quote?.trading_symbol && !quote?.error)
    .map((quote) => {
      return {
        name: quote.trading_symbol,
        price: quote.last_price ?? 0,
        change: quote.day_change ?? 0,
        lastTradeTime: quote.last_trade_time ?? null,
      };
    })
    .sort((a, b) => (securityOrder.get(a.name) ?? 999) - (securityOrder.get(b.name) ?? 999));
};

const MarqueeComponent = () => {
  const { isMobile, setIsMobile } = UseResponsiveStatus();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [marqueeData, setMarqueeData] = useState([]);
  const [marketStatus, setMarketStatus] = useState("Closed");
  const [marketReason, setMarketReason] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchQuotes = async () => {

    try {
      const response = await fetchGrowwQuotesBatch(MARQUEE_SECURITIES);
      const topPrices = mapGrowwQuotesToMarqueeItems(response?.data?.quotes);
      const latestTradeTime = topPrices.reduce((latest, item) => {
        if (!item.lastTradeTime) {
          return latest;
        }
        return Math.max(latest, item.lastTradeTime);
      }, 0);


      setMarqueeData(topPrices);
      setLastFetched(latestTradeTime ? new Date(latestTradeTime * 1000) : new Date());
      return topPrices;
    } catch (error) {
      console.error("Failed to fetch Groww marquee quotes:", error);

      setMarqueeData([]);
      setLastFetched(new Date());
      return [];
    }
  };

  useEffect(() => {
    let refreshTimer = null;

    const initialize = async () => {

      try {
        const statusResponse = await fetchMarketHolidayStatus(new Date().toISOString());
        const marketStatusPayload = statusResponse?.data?.marketStatus;

        if (marketStatusPayload) {
          setMarketStatus(marketStatusPayload.isOpen ? "Open" : "Closed");
          setMarketReason(marketStatusPayload.reason ?? null);

          if (marketStatusPayload.date) {
            setLastFetched(new Date(marketStatusPayload.date));
          }
        }
      } catch (error) {
        console.error("Failed to fetch market status:", error);
      }


      await fetchQuotes();
      refreshTimer = setInterval(fetchQuotes, REFRESH_INTERVAL_MS);
    };

    initialize();

    return () => {
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }
    };
  }, []);

  const handleClick = useCallback(
    (item) => {
      if (window.innerWidth < MOBILE_BREAKPOINT) {
        setIsMobile(true);
        setTimeout(() => setIsMobile(false), 5000);
      }

      const stockDetails = stockDetailsData[item.name];
      if (!stockDetails) {
        return;
      }

      dispatch(
        stockActions.setStock({
          code: stockDetails.scrip_code,
          stockName: item.name,
          stockLogo: stockDetails.logo,
          stockWebsite: stockDetails.website_url,
        })
      );
      navigate(`/stock/${item.name}`);
    },
    [dispatch, navigate, setIsMobile]
  );

  return (
    <section className="marquee-section">
      {isMobile && (
        <AppTooltip
          arrow
          placement="bottom-start"
          title={lastFetched ? (
            <StatusMessage
              marketStatus={marketStatus}
              occasion={marketReason}
              lastFetched={lastFetched}
            />
          ) : ""}
          tooltipSx={{
            width: 'max-content',
            maxWidth: 'calc(100vw - 32px)',
            boxSizing: 'border-box',
            overflowWrap: 'anywhere',
          }}
        >
          <div className="market-status-box">
            <MarketStatus marketStatus={marketStatus} />
            <div className="status-underline" />
          </div>
        </AppTooltip>
      )}
      <Marquee className="marquee-container dot-matrix-font" autoFill speed={100} pauseOnHover>
        {marqueeData.map((item, index) => (
          <MarqueeItem
            key={index}
            item={item}
            onClick={() => handleClick(item)}
          />
        ))}
      </Marquee>
    </section>
  );
};

export default MarqueeComponent;
